<template>
  <div class="h5-order-report">
    <b-container fluid class="content">
      <b-card no-body class="filter-panel">
        <b-card-body>
          <div class="filter-grid">
            <base-input :label="$t('From Time')">
              <flat-pickr
                :config="pickr_config"
                class="form-control"
                :placeholder="$t('From Time') + '...'"
                v-model="filters.start"
              >
              </flat-pickr>
            </base-input>
            <base-input :label="$t('To Time')">
              <flat-pickr
                :config="pickr_config"
                class="form-control"
                :placeholder="$t('To Time') + '...'"
                v-model="filters.end"
              >
              </flat-pickr>
            </base-input>
            <base-input v-if="!singleStore" :label="$t('Store')">
              <select class="form-control" v-model="filters.store_id">
                <option></option>
                <option
                  v-for="(store, idx) in storelist"
                  :key="idx"
                  :value="store.LocationID"
                >
                  {{ store.LocationName || store.LocationID }}
                </option>
              </select>
            </base-input>
            <base-input :label="$t('Keyword')">
              <input class="form-control" v-model="filters.keyword" @keyup.enter="search(1)" />
            </base-input>
            <base-input :label="$t('Order Type')">
              <select class="form-control" v-model="filters.order_type">
                <option></option>
                <option :value="0">{{ $t('Dine In') }}</option>
                <option :value="1">{{ $t('Pickup') }}</option>
                <option :value="2">{{ $t('Delivery') }}</option>
              </select>
            </base-input>
            <base-input :label="$t('Order Status')">
              <select class="form-control" v-model="filters.order_status">
                <option></option>
                <option
                  v-for="status in orderStatuses"
                  :key="status"
                  :value="status"
                >
                  {{ orderStatusName(status) }}
                </option>
              </select>
            </base-input>
            <base-input :label="$t('Payment Status')">
              <select class="form-control" v-model="filters.payment_status">
                <option></option>
                <option
                  v-for="status in paymentStatuses"
                  :key="status"
                  :value="status"
                >
                  {{ paymentStatusName(status) }}
                </option>
              </select>
            </base-input>
            <div class="filter-actions">
              <b-button variant="primary" @click="search(1)" :disabled="loading">{{ $t('Search') }}</b-button>
              <b-button variant="secondary" @click="search(page)" :disabled="loading">{{ $t('Refresh') }}</b-button>
              <b-button variant="outline-secondary" @click="resetFilters" :disabled="loading">{{ $t('Reset') }}</b-button>
              <b-button variant="success" @click="exportReport" :disabled="loading || exporting">{{ $t('Export') }}</b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-3">
        <b-card-body>
          <div class="table-responsive">
            <table class="table table-hover report-table">
              <thead>
                <tr>
                  <th>{{ $t('Order No') }}</th>
                  <th class="sortable" @click="toggleSort">{{ $t('Date Time') }}</th>
                  <th>{{ $t('Store') }}</th>
                  <th>{{ $t('Order Type') }}</th>
                  <th>{{ $t('Fulfillment') }}</th>
                  <th>{{ $t('Customer Name') }}</th>
                  <th>{{ $t('Member No') }}</th>
                  <th>{{ $t('Phone') }}</th>
                  <th class="text-right">{{ $t('Amount') }}</th>
                  <th>{{ $t('Payment Method') }}</th>
                  <th>{{ $t('Payment Status') }}</th>
                  <th>{{ $t('Order Status') }}</th>
                  <th>{{ $t('POS Status') }}</th>
                  <th>{{ $t('Action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in orders" :key="order.idn" @click="openDetail(order)">
                  <td><a href="javascript:;" @click.stop="copy(order.transaction_no)">{{ order.transaction_no }}</a></td>
                  <td>{{ order.created_at }}</td>
                  <td>{{ order.store_name || order.store_id }}</td>
                  <td>{{ orderTypeName(order.order_type) }}</td>
                  <td>{{ order.fulfillment || '-' }}</td>
                  <td>{{ order.customer_name || '-' }}</td>
                  <td>{{ order.customer_no || '-' }}</td>
                  <td>{{ order.customer_phone || '-' }}</td>
                  <td class="text-right">{{ money(order.total_amount, order.currency) }}</td>
                  <td>{{ paymentMethodName(order) }}</td>
                  <td><span class="status-pill">{{ paymentStatusName(order.payment_status) }}</span></td>
                  <td><span class="status-pill">{{ orderStatusName(order.order_status) }}</span></td>
                  <td>{{ posStatusName(order.pos_status_name) }}</td>
                  <td><b-button size="sm" variant="link" @click.stop="openDetail(order)">{{ $t('View') }}</b-button></td>
                </tr>
                <tr v-if="!orders.length && !loading">
                  <td colspan="14" class="text-center text-muted">{{ $t('No Data') }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pager">
            <span>{{ $t('Total') }}: {{ total }} | {{ $t('Page') }} {{ page }} / {{ totalPages }}</span>
            <div>
              <select class="form-control page-size" v-model.number="pageSize" @change="search(1)">
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
              <b-button size="sm" :disabled="page <= 1 || loading" @click="search(page - 1)">{{ $t('Prev') }}</b-button>
              <b-button size="sm" :disabled="page >= totalPages || loading" @click="search(page + 1)">{{ $t('Next') }}</b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-container>

    <div v-if="drawerOpen" class="drawer-mask" @click.self="drawerOpen = false">
      <aside class="detail-drawer">
        <header class="drawer-header">
          <div>
            <h3>{{ detail.transaction_no }}</h3>
            <div class="drawer-status">
              <span v-for="pill in drawerStatusPills" :key="pill.key">{{ pill.label }}</span>
            </div>
          </div>
          <button class="close-btn" @click="drawerOpen = false">x</button>
        </header>

        <div v-if="detailLoading" class="drawer-loading">{{ $t('Loading') }}</div>
        <div v-else class="drawer-body">
          <section class="detail-section">
            <div class="section-title">{{ $t('Basic Info') }}</div>
            <div class="info-grid">
              <label>{{ $t('Order No') }}</label><span>{{ detail.transaction_no }}</span>
              <label>{{ $t('Source') }}</label><span>pkh5</span>
              <label>{{ $t('Store') }}</label><span>{{ detail.store_name || detail.store_id }}</span>
              <label>{{ $t('Date Time') }}</label><span>{{ detail.created_at }}</span>
              <label>{{ $t('Order Type') }}</label><span>{{ orderTypeName(detail.order_type) }}</span>
              <label>{{ $t('Fulfillment') }}</label><span>{{ detail.fulfillment || '-' }}</span>
              <label>{{ $t('Target Table') }}</label><span>{{ detail.target_table_name || detail.target_table_id || '-' }}</span>
              <label>{{ $t('Actual Table') }}</label><span>{{ detail.actual_table_name || detail.actual_table_id || '-' }}</span>
              <label>{{ $t('Note') }}</label><span>{{ detail.note || '-' }}</span>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('POS Local Order') }}</div>
            <div class="info-grid">
              <label>{{ $t('Local Order') }}</label><span>{{ detail.pos_local_order_no || detail.pos_local_order_id || '-' }}</span>
              <label>{{ $t('POS Status') }}</label><span>{{ posStatusName(detail.pos_status_name) }}</span>
              <label>{{ $t('On POS') }}</label><span>{{ yesNo(detail.on_pos) }}</span>
              <label>{{ $t('Table Visible') }}</label><span>{{ yesNo(detail.table_visible) }}</span>
              <label>{{ $t('Financial Lock') }}</label><span>{{ yesNo(detail.financial_lock) }}</span>
              <label>{{ $t('Business Date') }}</label><span>{{ detail.business_date || '-' }}</span>
              <label>{{ $t('Day End Included') }}</label><span>{{ yesNo(detail.day_end_included) }}</span>
              <label>{{ $t('Day End Exclusion Reason') }}</label><span>{{ detail.day_end_exclusion_reason || '-' }}</span>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Customer') }}</div>
            <div class="info-grid">
              <label>{{ $t('Customer Name') }}</label><span>{{ detail.customer_name || '-' }}</span>
              <label>{{ $t('Phone') }}</label><span>{{ detail.customer_phone || '-' }}</span>
              <label>{{ $t('Member No') }}</label><span>{{ detail.customer_no || '-' }}</span>
              <label>{{ $t('Member Level') }}</label><span>{{ detail.member_level || '-' }}</span>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Payment Info') }}</div>
            <div class="info-grid">
              <label>{{ $t('Payment Channel') }}</label><span>{{ paymentChannelName(detail.payment_channel) }}</span>
              <label>{{ $t('Payment Method') }}</label><span>{{ paymentMethodName(detail) }}</span>
              <label>{{ $t('Payment Status') }}</label><span>{{ paymentStatusName(detail.payment_status) }}</span>
              <label>{{ $t('Paid Amount') }}</label><span>{{ money(detail.paid_amount, detail.currency) }}</span>
              <label>{{ $t('Payment ID') }}</label><span>{{ detail.payment_id || '-' }}</span>
              <label>{{ $t('Paid Time') }}</label><span>{{ detail.paid_at || '-' }}</span>
              <label>{{ $t('POS Tender') }}</label><span>{{ detail.pos_tender || '-' }}</span>
              <label>{{ $t('Refundable Amount') }}</label><span>{{ money(detail.refundable_amount, detail.currency) }}</span>
              <label>{{ $t('Refund Unavailable Reason') }}</label>
              <span>{{ unavailableReason }}</span>
            </div>
            <div v-if="canRefund" class="refund-inline">
              <base-input v-if="showRefundReason" class="refund-reason" :label="$t('Refund Reason')">
                <select class="form-control" v-model="refund.reason">
                  <option v-for="option in refundReasonOptions" :key="option.value" :value="option.value">
                    {{ $t(option.text) }}
                  </option>
                </select>
              </base-input>
              <base-input v-if="showRefundReason && refund.reason === 'other'" class="refund-note" :label="$t('Reason Note')">
                <textarea class="form-control" rows="1" v-model="refund.note"></textarea>
              </base-input>
              <b-button
                class="refund-button"
                variant="danger"
                :disabled="refundLoading"
                @click="confirmRefund"
              >
                {{ $t('Full Refund') }}
              </b-button>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Items') }}</div>
            <div v-for="(item, idx) in detail.items" :key="idx" class="line-item">
              <div class="line-main">
                <strong>{{ item.Name1 || item.name || item.ProductName || '-' }}</strong>
                <span>x {{ item.Qty || item.qty || 1 }}</span>
                <span>{{ money(item.ItemSubTotal || item.subtotal || item.amount || 0, detail.currency) }}</span>
              </div>
              <div v-if="item.SelectedModifiers || item.modifiers" class="line-sub">
                {{ modifierText(item.SelectedModifiers || item.modifiers) }}
              </div>
              <div v-if="item.SelectedCombo || item.combo_lines" class="line-sub">
                {{ modifierText(item.SelectedCombo || item.combo_lines) }}
              </div>
              <div v-if="item.Note || item.note" class="line-sub">{{ item.Note || item.note }}</div>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Totals') }}</div>
            <div class="info-grid">
              <template v-for="(totalRow, idx) in detail.totals">
                <label :key="'l'+idx">{{ totalRow.Name1 || totalRow.Code || totalRow.code }}</label>
                <span :key="'v'+idx">{{ money(totalRow.Amount || totalRow.amount || 0, detail.currency) }}</span>
              </template>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Timeline') }}</div>
            <div v-for="log in detail.logs" :key="log.IDn" class="timeline-row">
              <span>{{ log.CreatedAt }}</span>
              <strong>{{ log.EventType }}</strong>
              <em>{{ log.EventSource }}</em>
              <p>{{ log.Message }}</p>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ $t('Payment Logs') }}</div>
            <div v-for="payment in detail.payment_logs" :key="payment.IDn" class="timeline-row">
              <span>{{ payment.CreatedAt }}</span>
              <strong>{{ payment.PaymentType }} / {{ payment.PaymentStatus }}</strong>
              <em>{{ money(payment.PaymentAmount, payment.Currency) }}</em>
              <p>{{ payment.GatewayTransactionNo }} {{ payment.GatewayReference }}</p>
            </div>
          </section>
        </div>
      </aside>
    </div>

  </div>
</template>

<script>
const axios = require("axios");
import { getStore } from "../../util/helper";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  components: {
    [flatPicker.name]: flatPicker,
  },
  data() {
    return {
      pickr_config: {
        enableTime: true,
        time_24hr: true,
        allowInput: true,
        minuteIncrement: 30,
        dateFormat: "Y-m-d H:i",
        maxDate: new Date().fp_incr(1),
      },
      token: "",
      userId: 0,
      loading: false,
      exporting: false,
      detailLoading: false,
      refundLoading: false,
      canEdit: false,
      singleStore: false,
      storelist: [],
      orders: [],
      total: 0,
      page: 1,
      pageSize: 50,
      sortDir: "DESC",
      drawerOpen: false,
      detail: {},
      filters: {
        start: "",
        end: "",
        store_id: "",
        store_ids: [],
        order_type: "",
        order_status: "",
        payment_status: "",
        keyword: "",
      },
      refund: {
        reason: "customer_request",
        note: "",
      },
      refundReasonOptions: [
        { value: "customer_request", text: "Customer Request" },
        { value: "out_of_stock", text: "Out of Stock" },
        { value: "duplicate_order", text: "Duplicate Order" },
        { value: "order_error", text: "Order Error" },
        { value: "store_unable_to_fulfill", text: "Store Unable To Fulfill" },
        { value: "other", text: "Other" },
      ],
      orderStatuses: ["created", "submitted", "confirmed", "ready", "token", "delivering", "finish", "completed", "reject", "poscancel", "abnormal", "cancelled"],
      paymentStatuses: ["pending", "unpaid", "paid", "failed", "refunding", "refunded"],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    canRefund() {
      return Boolean(this.canEdit && this.detail.refund_eligibility && this.detail.refund_eligibility.can_refund);
    },
    showRefundReason() {
      if (!this.canRefund || !this.detail.refund_eligibility) {
        return false;
      }
      return this.detail.refund_eligibility.requires_reason !== false;
    },
    unavailableReason() {
      if (!this.detail.refund_eligibility || !this.detail.refund_eligibility.reasons || !this.detail.refund_eligibility.reasons.length) {
        return this.canRefund ? "-" : "";
      }
      return this.detail.refund_eligibility.reasons.join(" ");
    },
    drawerStatusPills() {
      const candidates = [
        { key: "order", label: this.orderStatusName(this.detail.order_status) },
        { key: "payment", label: this.paymentStatusName(this.detail.payment_status) },
        { key: "pos", label: this.posStatusName(this.detail.pos_status_name) },
      ];
      const used = {};
      return candidates.filter(item => {
        const label = item.label || "-";
        const key = this.statusPillKey(label);
        if (!label || label === "-" || used[key]) {
          return false;
        }
        used[key] = true;
        return true;
      });
    },
  },
  created() {
    this.token = getStore("token");
    this.userId = getStore("user_id");
    this.init();
  },
  methods: {
    formData() {
      const fd = new FormData();
      fd.append("token", this.token);
      fd.append("user_id", this.userId);
      fd.append("StartTime", this.toServerDateTime(this.filters.start));
      fd.append("EndTime", this.toServerDateTime(this.filters.end));
      fd.append("StoreIDs", JSON.stringify(this.selectedStoreIds()));
      fd.append("OrderTypes", JSON.stringify(this.selectedValueList(this.filters.order_type)));
      fd.append("OrderStatuses", JSON.stringify(this.selectedValueList(this.filters.order_status)));
      fd.append("PaymentStatuses", JSON.stringify(this.selectedValueList(this.filters.payment_status)));
      fd.append("Keyword", this.filters.keyword);
      fd.append("page", this.page);
      fd.append("page_size", this.pageSize);
      fd.append("sort_dir", this.sortDir);
      return fd;
    },
    init() {
      const fd = new FormData();
      fd.append("token", this.token);
      fd.append("user_id", this.userId);
      axios.post("/Api/Web/Online_order_report", fd).then(response => {
        const rt = response.data;
        if (rt.status !== 0) {
          alert(rt.message || this.$t("Error"));
          return;
        }
        this.storelist = rt.data.storelist || [];
        this.singleStore = Boolean(rt.data.single_store);
        this.canEdit = Boolean(rt.data.can_edit);
        this.filters.store_ids = this.storelist.map(store => store.LocationID);
        this.filters.store_id = this.singleStore && this.storelist.length ? this.storelist[0].LocationID : "";
        this.filters.start = this.toInputDateTime(rt.data.default_start);
        this.filters.end = this.toInputDateTime(rt.data.default_end);
        this.search(1);
      }).catch(() => {
        this.$router.push("/login");
      });
    },
    search(page) {
      this.page = page || 1;
      this.loading = true;
      axios.post("/Api/Web/Online_order_report/search", this.formData()).then(response => {
        this.loading = false;
        const rt = response.data;
        if (rt.status !== 0) {
          alert(rt.message || this.$t("Error"));
          return;
        }
        this.orders = rt.data.items || [];
        this.total = rt.data.total || 0;
        this.page = rt.data.page || this.page;
        this.pageSize = rt.data.page_size || this.pageSize;
      }).catch(() => {
        this.loading = false;
        alert(this.$t("Error"));
      });
    },
    openDetail(order) {
      this.drawerOpen = true;
      this.detailLoading = true;
      this.detail = order || {};
      this.resetRefund();
      const fd = new FormData();
      fd.append("token", this.token);
      fd.append("user_id", this.userId);
      fd.append("IDn", order.idn);
      axios.post("/Api/Web/Online_order_report/detail", fd).then(response => {
        this.detailLoading = false;
        const rt = response.data;
        if (rt.status !== 0) {
          alert(rt.message || this.$t("Error"));
          return;
        }
        this.detail = rt.data.order || {};
        this.canEdit = Boolean(this.detail.can_edit);
      }).catch(() => {
        this.detailLoading = false;
        alert(this.$t("Error"));
      });
    },
    exportReport() {
      this.exporting = true;
      axios.post("/Api/Web/Online_order_report/export", this.formData(), { responseType: "blob" }).then(response => {
        this.exporting = false;
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "h5_order_report.csv";
        link.click();
        window.URL.revokeObjectURL(url);
      }).catch(() => {
        this.exporting = false;
        alert(this.$t("Error"));
      });
    },
    resetRefund() {
      this.refund = { reason: "customer_request", note: "" };
    },
    confirmRefund() {
      if (this.showRefundReason && this.refund.reason === "other" && !this.refund.note) {
        alert(this.$t("Reason Note") + " " + this.$t("Required"));
        return;
      }
      if (!confirm(this.$t("Confirm Refund"))) {
        return;
      }
      this.refundLoading = true;
      const fd = new FormData();
      fd.append("token", this.token);
      fd.append("user_id", this.userId);
      fd.append("IDn", this.detail.idn);
      if (this.showRefundReason) {
        fd.append("reason", this.refund.reason);
        fd.append("reason_note", this.refund.note);
      }
      fd.append("IdempotencyKey", this.detail.transaction_no + "-" + Date.now());
      axios.post("/Api/Web/Online_order_report/refund", fd).then(response => {
        this.refundLoading = false;
        const rt = response.data;
        if (rt.status !== 0) {
          alert(rt.message || this.$t("Error"));
          return;
        }
        this.openDetail(this.detail);
        this.search(this.page);
      }).catch(() => {
        this.refundLoading = false;
        alert(this.$t("Error"));
      });
    },
    resetFilters() {
      this.filters.store_ids = this.storelist.map(store => store.LocationID);
      this.filters.store_id = this.singleStore && this.storelist.length ? this.storelist[0].LocationID : "";
      this.filters.order_type = "";
      this.filters.order_status = "";
      this.filters.payment_status = "";
      this.filters.keyword = "";
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      this.filters.start = `${y}-${m}-${d} 00:00`;
      this.filters.end = `${y}-${m}-${d} 23:59`;
      this.search(1);
    },
    selectedStoreIds() {
      if (this.filters.store_id) {
        return [this.filters.store_id];
      }
      return [];
    },
    selectedValueList(value) {
      return value === "" || value === null || value === undefined ? [] : [value];
    },
    toggleSort() {
      this.sortDir = this.sortDir === "DESC" ? "ASC" : "DESC";
      this.search(1);
    },
    toInputDateTime(value) {
      return this.normalizeDateTimeText(value);
    },
    toServerDateTime(value) {
      return value ? value.replace("T", " ") + (value.length === 16 ? ":00" : "") : "";
    },
    normalizeDateTimeText(value) {
      if (!value) {
        return "";
      }
      const normalized = String(value).trim().replace("T", " ");
      const match = normalized.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})/);
      return match ? `${match[1]} ${match[2]}:${match[3]}` : normalized.substring(0, 16);
    },
    money(amount, currency) {
      return `${currency || ""} ${Number(amount || 0).toFixed(2)}`;
    },
    copy(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      }
    },
    orderTypeName(type) {
      if (Number(type) === 1) return this.$t("Pickup");
      if (Number(type) === 2) return this.$t("Delivery");
      return this.$t("Dine In");
    },
    orderStatusName(status) {
      const map = {
        created: "Created",
        submitted: "Submitted",
        confirmed: "Confirmed",
        ready: "Ready",
        token: "Picked Up",
        delivering: "Delivering",
        finish: "Completed",
        completed: "Completed",
        reject: "Rejected",
        poscancel: "Merchant Cancelled",
        abnormal: "Abnormal",
        cancelled: "Cancelled",
      };
      return this.$t(map[status] || status || "-");
    },
    paymentStatusName(status) {
      const map = {
        pending: "Pending",
        unpaid: "Unpaid",
        paid: "Paid",
        failed: "Failed",
        refunding: "Refunding",
        refunded: "Refunded",
      };
      return this.$t(map[status] || status || "-");
    },
    posStatusName(status) {
      return this.$t(status || "-");
    },
    statusPillKey(label) {
      const text = String(label || "").trim().toLowerCase();
      if (["completed", "finished", "已完成", "完成", "已完成"].indexOf(text) >= 0) {
        return "completed";
      }
      return text;
    },
    paymentChannelName(channel) {
      if (channel === "online_gateway") return this.$t("Online Gateway");
      if (channel === "pos") return this.$t("POS");
      return this.$t("Pay At Store");
    },
    paymentMethodName(order) {
      if (!order) return "-";
      const value = order.payment_method || this.paymentChannelName(order.payment_channel);
      const normalized = String(value || "").trim().toLowerCase();
      if (["pay_at_store", "pay at store", "到店付款"].indexOf(normalized) >= 0) {
        return "POS";
      }
      if (["credit_card", "credit card", "网上支付网关", "online gateway"].indexOf(normalized) >= 0) {
        return "Online";
      }
      return value || "-";
    },
    yesNo(value) {
      return Number(value) ? this.$t("Yes") : this.$t("No");
    },
    modifierText(list) {
      if (!Array.isArray(list)) return "";
      return list.map(item => item.Name1 || item.name || item.ProductName || "").filter(Boolean).join(", ");
    },
  },
};
</script>

<style scoped>
.h5-order-report .content {
  padding-top: 88px;
}
.h5-order-report .filter-panel {
  margin-top: 0;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px 16px;
  align-items: end;
}
.filter-actions {
  grid-column: 1 / -1;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-left: 50%;
  min-height: 64px;
}
.report-table {
  font-size: 13px;
  min-width: 1280px;
}
.report-table tbody tr {
  cursor: pointer;
}
.sortable {
  cursor: pointer;
}
.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 8px;
  background: #eef2f7;
  color: #253044;
}
.pager {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.pager > div {
  display: flex;
  gap: 8px;
  align-items: center;
}
.page-size {
  width: 82px;
}
.drawer-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.22);
  display: flex;
  justify-content: flex-end;
}
.detail-drawer {
  width: min(760px, 100vw);
  height: 100vh;
  background: #fff;
  box-shadow: -12px 0 30px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}
.drawer-header {
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}
.drawer-header h3 {
  margin: 0 0 8px;
  font-size: 20px;
}
.drawer-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.drawer-status span {
  background: #edf2f7;
  border-radius: 8px;
  padding: 4px 8px;
}
.close-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d6dbe3;
  background: #fff;
  border-radius: 6px;
}
.drawer-body {
  overflow: auto;
  padding: 18px 22px 32px;
}
.drawer-loading {
  padding: 24px;
}
.detail-section {
  border-bottom: 1px solid #edf0f4;
  padding: 14px 0;
}
.section-title {
  font-weight: 700;
  margin-bottom: 10px;
}
.info-grid {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px 14px;
}
.info-grid label {
  color: #667085;
  margin: 0;
}
.line-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.line-main {
  display: grid;
  grid-template-columns: 1fr 70px 110px;
  gap: 10px;
}
.line-sub {
  margin-top: 6px;
  color: #667085;
  font-size: 13px;
}
.timeline-row {
  padding: 8px 0;
  border-bottom: 1px dashed #e5e7eb;
}
.timeline-row span,
.timeline-row em {
  color: #667085;
  margin-right: 8px;
}
.timeline-row p {
  margin: 4px 0 0;
}
.refund-inline {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 12px;
}
.refund-reason {
  min-width: 210px;
}
.refund-note {
  flex: 1 1 220px;
}
.refund-button {
  margin-bottom: 1rem;
}
@media (max-width: 992px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}
@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    margin-left: 0;
  }
  .pager {
    align-items: flex-start;
    flex-direction: column;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
